// SPEC-213 · Gestión del Círculo de Miembros. Oráculo visual:
// context/prototype/source/admin.jsx:1784-1982 (AdminCirculoMiembros) y el
// contrato `portal.circle-management` de
// context/agent-os/visual-validation/screens.json. Permisos: ANNEX-015
// §2.4 — el módulo no agrega fila a la matriz, se gobierna con el permiso
// sobre `members` (Ver: buscar y consultar; Editar: alta, edición,
// inactivación, activación y registro/anulación de acciones).
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Can } from "@core/permissions";
import { tableSpacing } from "@core/table";
import { tokens } from "@core/theme/tokens";
import {
  Badge,
  DialogHeader,
  Field,
  FeedbackState,
  ImpactConfirmDialog,
  inputStyle,
  Modal,
  PortalButton,
  RowIconAction,
  useToast,
} from "@portal/common";
import { reportError } from "@portal/common/errorMessage";
import { formatShortDate } from "../vehicles/dateFormat";
import { ReferralActionsPanel } from "./ReferralActionsPanel";
import {
  REFERRAL_CRUD_ERROR_MESSAGES,
  type MemberSummary,
  type ReferralCircleEntry,
  type ReferralInput,
} from "./api";
import {
  useCircleMutations,
  useMemberSearch,
  useReferralCircle,
} from "./referralQueries";

const NO_REFERRAL_CONTACT_MESSAGE =
  "Ingresa al menos un dato de contacto del referido: correo o teléfono.";

const referralSchema = z
  .object({
    name: z.string().trim().min(1, "El nombre es obligatorio."),
    phone: z.string().optional(),
    email: z
      .string()
      .optional()
      .refine(
        (value) =>
          !value?.trim() || z.string().email().safeParse(value.trim()).success,
        { message: "Ingresa un correo válido." },
      ),
  })
  .refine((value) => Boolean(value.phone?.trim() || value.email?.trim()), {
    // D-5/RF-CIR-03: falta uno de los dos datos, no uno específico — el
    // mensaje no se ata a un campo (`path: []`) para no señalar Correo
    // cuando el usuario quería completar Teléfono.
    message: NO_REFERRAL_CONTACT_MESSAGE,
    path: [],
  });
type ReferralFormValues = z.infer<typeof referralSchema>;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

function MemberSearch({
  onSelect,
}: {
  onSelect: (member: MemberSummary) => void;
}) {
  const [query, setQuery] = useState("");
  const results = useMemberSearch(query);
  const trimmed = query.trim();
  return (
    <>
      <div
        style={{
          maxWidth: 560,
          margin: "10px auto 0",
          padding: 20,
          border: `1px solid ${tokens.surface.line}`,
          borderRadius: 18,
          background: tokens.surface.bg2,
        }}
      >
        <div className="label" style={{ marginBottom: 10 }}>
          Buscar miembro
        </div>
        <input
          autoFocus
          aria-label="Buscar miembro"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Nombre, identificación o correo…"
          style={{ ...inputStyle, padding: "13px 15px" }}
        />
        <div
          style={{
            font: `400 11.5px/1.5 ${tokens.font.ui}`,
            color: tokens.text.disabled,
            marginTop: 10,
          }}
        >
          Escribe para buscar. Son muchos miembros para mostrarlos todos en un
          listado.
        </div>
      </div>

      {trimmed.length > 0 ? (
        <div
          style={{
            maxWidth: 560,
            margin: "14px auto 0",
            border: `1px solid ${tokens.surface.line}`,
            borderRadius: 18,
            overflow: "hidden",
            background: tokens.surface.bg2,
          }}
        >
          {results.isLoading ? (
            <FeedbackState kind="loading" />
          ) : (results.data ?? []).length === 0 ? (
            <div
              style={{
                padding: "30px 20px",
                textAlign: "center",
                font: `400 12.5px ${tokens.font.ui}`,
                color: tokens.text.disabled,
              }}
            >
              Ningún miembro coincide con «{trimmed}».
            </div>
          ) : (
            (results.data ?? []).map((member, index) => (
              <div
                key={member.id}
                onClick={() => onSelect(member)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "13px 18px",
                  borderTop: index
                    ? `1px solid ${tokens.surface.line}`
                    : "none",
                  cursor: "pointer",
                }}
              >
                <div
                  className="av"
                  style={{ width: 32, height: 32, fontSize: 11, flex: "none" }}
                >
                  {initials(member.legalName)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `600 13px ${tokens.font.ui}` }}>
                    {member.legalName}
                  </div>
                  <div
                    style={{
                      font: `400 11px ${tokens.font.ui}`,
                      color: tokens.text.disabled,
                      marginTop: 2,
                    }}
                  >
                    {[member.identification, member.email]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <Badge tone={member.active ? "silver" : "neutral"}>
                  {member.active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            ))
          )}
        </div>
      ) : null}
    </>
  );
}

function ReferralForm({
  title,
  subtitle,
  initial,
  submitting,
  onCancel,
  onSubmit,
  modal,
}: {
  title: string;
  subtitle: string;
  initial?: ReferralInput;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (value: ReferralInput) => void;
  // El formulario ES el diálogo (revisión en vivo 2026-09-22 punto #15): el
  // encabezado y el pie que antes dibujaba a mano, con sus divisores, son
  // ahora las regiones fijas del `Modal`.
  modal: { label: string; width?: number | string; onClose: () => void };
}) {
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<ReferralFormValues>({
    defaultValues: {
      name: initial?.name ?? "",
      phone: initial?.phone ?? "",
      email: initial?.email ?? "",
    },
  });
  const name = watch("name");
  // PLAN-009 §3.6: cada issue de zod cae bajo SU campo — antes,
  // `issues[0]` mostraba un solo banner aunque el issue fuera de otro campo
  // (patrón de SchedulingScreen.tsx). El refine cruzado ("falta contacto",
  // `path: []`) no señala un campo específico — se asigna a Teléfono, el
  // primero de los dos datos de contacto y donde ya vive el hint que
  // explica la regla.
  const submit = (value: ReferralFormValues) => {
    const parsed = referralSchema.safeParse(value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const target: keyof ReferralFormValues =
          issue.path[0] === "name" || issue.path[0] === "email"
            ? issue.path[0]
            : "phone";
        setError(target, { message: issue.message });
      }
      return;
    }
    onSubmit({
      name: parsed.data.name.trim(),
      phone: parsed.data.phone?.trim() || null,
      email: parsed.data.email?.trim() || null,
    });
  };
  return (
    // `display: contents` + el `<form>` por fuera del `Modal`: el botón
    // "Guardar" vive en el pie fijo y sigue siendo descendiente del
    // formulario, así que `onSubmit` se dispara igual que antes.
    <form
      onSubmit={handleSubmit(submit)}
      noValidate
      style={{ display: "contents" }}
    >
      <Modal
        label={modal.label}
        {...(modal.width !== undefined ? { width: modal.width } : {})}
        onClose={modal.onClose}
        // Punto #21 (revisión en vivo 2026-09-22): como en "Nueva
        // recompensa", la acción va de eyebrow y el título es el nombre que se
        // está escribiendo (o el actual); el círculo sigue debajo.
        header={
          <>
            <DialogHeader
              eyebrow={title}
              title={name || "Referido sin nombre"}
            />
            <div
              style={{
                font: `400 12px ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                marginTop: 5,
              }}
            >
              {subtitle}
            </div>
          </>
        }
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn-link" onClick={onCancel}>
              Cancelar
            </button>
            <PortalButton tone="primary" size="small" disabled={submitting}>
              Guardar
            </PortalButton>
          </div>
        }
      >
        <Field label="Nombre" required error={errors.name?.message}>
          <input aria-label="Nombre" style={inputStyle} {...register("name")} />
        </Field>
        <Field
          label="Teléfono"
          hint={NO_REFERRAL_CONTACT_MESSAGE}
          error={errors.phone?.message}
        >
          <input
            aria-label="Teléfono"
            style={inputStyle}
            {...register("phone")}
          />
        </Field>
        <Field label="Correo" error={errors.email?.message}>
          <input
            aria-label="Correo"
            style={inputStyle}
            {...register("email")}
          />
        </Field>
      </Modal>
    </form>
  );
}

export function CircleManagementScreen() {
  const toast = useToast();
  const [member, setMember] = useState<MemberSummary | null>(null);
  const [viewing, setViewing] = useState<ReferralCircleEntry | null>(null);
  const [editing, setEditing] = useState<
    ReferralCircleEntry | null | undefined
  >(undefined);
  const [deactivating, setDeactivating] = useState<ReferralCircleEntry | null>(
    null,
  );
  const [openReferralId, setOpenReferralId] = useState<string | null>(null);

  const circle = useReferralCircle(member?.id ?? null);
  const mutations = useCircleMutations(member?.id ?? null);
  const rows = circle.data ?? [];

  const changeMember = () => {
    setMember(null);
  };

  const submitForm = (input: ReferralInput) => {
    if (editing?.id) {
      mutations.edit.mutate(
        { referralId: editing.id, input },
        {
          onSuccess: () => {
            setEditing(undefined);
            toast.show("Referido actualizado.");
          },
          onError: (error) =>
            reportError(toast, error, REFERRAL_CRUD_ERROR_MESSAGES),
        },
      );
      return;
    }
    mutations.create.mutate(input, {
      onSuccess: () => {
        setEditing(undefined);
        toast.show(
          `Referido creado para ${member?.legalName ?? ""} · use «Registrar acción» para su primera acción`,
        );
      },
      onError: (error) =>
        reportError(toast, error, REFERRAL_CRUD_ERROR_MESSAGES),
    });
  };

  const confirmDeactivate = () => {
    if (!deactivating) return;
    mutations.inactivate.mutate(deactivating.id, {
      onSuccess: () => {
        setDeactivating(null);
        toast.show(`${deactivating.name} inactivado`);
      },
      onError: (error) => {
        setDeactivating(null);
        reportError(toast, error, REFERRAL_CRUD_ERROR_MESSAGES);
      },
    });
  };

  return (
    <div data-testid="circle-management-visual-content">
      <header style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, font: `300 34px ${tokens.font.display}` }}>
          Gestión del Círculo de Miembros
        </h1>
        <p
          style={{
            color: tokens.text.tertiary,
            margin: "2px 0 0",
            fontSize: 13,
          }}
        >
          Busque un miembro y administre a sus referidos: alta, edición y
          registro de acciones.
        </p>
      </header>

      {!member ? (
        <MemberSearch onSelect={setMember} />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: 18,
              marginBottom: 18,
              border: `1px solid ${tokens.surface.line}`,
              borderRadius: 18,
              background: tokens.surface.bg2,
            }}
          >
            <div
              className="av"
              style={{ width: 44, height: 44, fontSize: 14, flex: "none" }}
            >
              {initials(member.legalName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: `600 15px ${tokens.font.ui}` }}>
                {member.legalName}
              </div>
              <div
                style={{
                  font: `400 11.5px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                  marginTop: 3,
                }}
              >
                {[member.identification, member.city, member.email]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button className="chip" onClick={changeMember}>
              Cambiar miembro
            </button>
            <Can module="members" action="canCreate">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setEditing(null)}
              >
                Nuevo referido
              </button>
            </Can>
          </div>

          <div
            style={{
              border: `1px solid ${tokens.surface.line}`,
              borderRadius: 18,
              overflow: "hidden",
              background: tokens.surface.bg2,
            }}
          >
            {circle.isLoading ? (
              <FeedbackState kind="loading" />
            ) : circle.isError ? (
              <FeedbackState kind="error" />
            ) : rows.length === 0 ? (
              <div style={{ padding: "52px 24px 62px", textAlign: "center" }}>
                <h3
                  className="display"
                  style={{ fontSize: 19, margin: "0 0 8px" }}
                >
                  Sin referidos todavía
                </h3>
                <div
                  style={{
                    font: `400 12.5px/1.6 ${tokens.font.ui}`,
                    color: tokens.text.tertiary,
                    maxWidth: 340,
                    margin: "0 auto 18px",
                  }}
                >
                  {member.legalName} no tiene referidos registrados en su
                  círculo.
                </div>
                <Can module="members" action="canCreate">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditing(null)}
                  >
                    Registrar el primer referido
                  </button>
                </Can>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1.4fr 130px 150px 130px 250px",
                    gap: 14,
                    alignItems: "center",
                    padding: "16px 20px 11px",
                    borderBottom: `1px solid ${tokens.surface.line}`,
                  }}
                >
                  {[
                    "Referido",
                    "Contacto",
                    "Invitado el",
                    "Etapa actual",
                    "Estado",
                  ].map((header) => (
                    <span key={header} className="label">
                      {header}
                    </span>
                  ))}
                  <span className="label" style={{ textAlign: "right" }}>
                    Acciones
                  </span>
                </div>
                {rows.map((row, index) => (
                  <div
                    key={row.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "1.5fr 1.4fr 130px 150px 130px 250px",
                      gap: 14,
                      alignItems: "center",
                      padding: "12px 20px",
                      borderTop: index
                        ? `1px solid ${tokens.surface.line}`
                        : "none",
                      opacity: row.active ? 1 : 0.55,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        minWidth: 0,
                      }}
                    >
                      <div
                        className="av"
                        style={{
                          width: 26,
                          height: 26,
                          fontSize: 9.5,
                          flex: "none",
                        }}
                      >
                        {initials(row.name)}
                      </div>
                      <span
                        style={{
                          font: `600 12.5px ${tokens.font.ui}`,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.name}
                      </span>
                    </div>
                    <span
                      style={{
                        font: `400 11.5px ${tokens.font.ui}`,
                        color: tokens.text.tertiary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.email || row.phone || "—"}
                    </span>
                    <span
                      style={{
                        font: `400 12px ${tokens.font.ui}`,
                        color: tokens.text.secondary,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatShortDate(row.addedOn)}
                    </span>
                    <span>
                      {row.currentStage ? (
                        <Badge tone="neutral">{row.currentStage}</Badge>
                      ) : (
                        <span
                          style={{
                            font: `400 11.5px ${tokens.font.ui}`,
                            color: tokens.text.disabled,
                          }}
                        >
                          Sin acciones
                        </span>
                      )}
                    </span>
                    <span
                      className={`stat stat-${row.active ? "silver" : "neutral"}`}
                    >
                      {row.active ? "Activo" : "Inactivo"}
                    </span>
                    {/* Revisión en vivo 2026-09-22, punto #10: cuatro
                      acciones por fila y tres formas distintas de dibujar
                      "esto es una acción" — "Ver" era `.btn-link` (texto
                      plano), las tres siguientes chips, y "Registrar acción"
                      sola mide ~140px, así que el grupo desbordaba la
                      columna de 250px y `flexWrap` lo partía en dos filas.
                      Con ícono + tooltip el grupo mide 4×40 + 3×6 = 178px:
                      entra, y las cuatro acciones se leen como hermanas. Se
                      quita el `flexWrap` por lo mismo que en Inicio: si
                      vuelve a no entrar, tiene que verse. */}
                    <div
                      style={{
                        display: "flex",
                        gap: tableSpacing.actionGap,
                        justifyContent: "flex-end",
                      }}
                    >
                      <RowIconAction
                        label="Ver"
                        icon="eye"
                        onClick={() => setViewing(row)}
                      />
                      <Can module="members" action="canEdit">
                        <RowIconAction
                          label="Editar"
                          icon="edit"
                          onClick={() => setEditing(row)}
                        />
                      </Can>
                      <Can module="members" action="canInactivate">
                        <RowIconAction
                          label={row.active ? "Inactivar" : "Activar"}
                          icon={row.active ? "deactivate" : "check"}
                          onClick={() => {
                            if (row.active) {
                              setDeactivating(row);
                            } else {
                              mutations.activate.mutate(row.id, {
                                onSuccess: () =>
                                  toast.show(`${row.name} activado`),
                                onError: (error) =>
                                  reportError(
                                    toast,
                                    error,
                                    REFERRAL_CRUD_ERROR_MESSAGES,
                                  ),
                              });
                            }
                          }}
                        />
                      </Can>
                      <Can module="members" action="canCreate">
                        <RowIconAction
                          label="Registrar acción"
                          icon="plus"
                          tone="accent"
                          onClick={() => setOpenReferralId(row.id)}
                        />
                      </Can>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}

      {/* Ver referido (solo lectura) — admin.jsx:1911-1931 */}
      {viewing ? (
        <Modal
          label="Ver referido"
          width={460}
          onClose={() => setViewing(null)}
          // Revisión en vivo 2026-09-22, punto #15: este diálogo dibujaba a
          // mano el encabezado y el pie con sus divisores y sus paddings.
          // Ahora los pone el `Modal`; acá quedan solo los contenidos.
          // Punto #21 (misma revisión): encabezado común de diálogos.
          header={<DialogHeader eyebrow="Ver referido" title={viewing.name} />}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn-link" onClick={() => setViewing(null)}>
                Cerrar
              </button>
            </div>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "14px 16px",
            }}
          >
            {(
              [
                ["Teléfono", viewing.phone || "—"],
                ["Correo", viewing.email || "—"],
                ["Referido por", member?.legalName ?? "—"],
                ["Invitado el", formatShortDate(viewing.addedOn)],
                ["Estado", viewing.active ? "Activo" : "Inactivo"],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <div
                  className="label"
                  style={{ fontSize: 9.5, marginBottom: 4 }}
                >
                  {label}
                </div>
                <div
                  style={{
                    font: `400 12.5px ${tokens.font.ui}`,
                    color: tokens.text.secondary,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      ) : null}

      {/* Nuevo / editar referido — admin.jsx:1933-1955 */}
      {editing !== undefined ? (
        <ReferralForm
          modal={{
            label: editing?.id ? "Editar referido" : "Nuevo referido",
            width: 520,
            onClose: () => setEditing(undefined),
          }}
          title={editing?.id ? "Editar referido" : "Nuevo referido"}
          subtitle={`Círculo de ${member?.legalName ?? ""}`}
          {...(editing
            ? {
                initial: {
                  name: editing.name,
                  phone: editing.phone ?? null,
                  email: editing.email ?? null,
                },
              }
            : {})}
          submitting={mutations.create.isPending || mutations.edit.isPending}
          onCancel={() => setEditing(undefined)}
          onSubmit={submitForm}
        />
      ) : null}

      {/* Confirmar inactivación — admin.jsx:1957-1971, mensaje de SPEC-213 §6
          (CONFIRM_INACTIVATE_REFERRAL), no el del prototipo (ANNEX-015 §2.2/
          §3, RN-07: inactivar no revierte avance ni puntos). */}
      {/* Revisión en vivo 2026-09-22, punto #15: esta confirmación estaba
          reconstruida a mano —con un `<div style={{ padding: 24 }}>` dentro de
          un `Modal` que ya trae el suyo, 48px en total— teniendo el diálogo
          compartido disponible. `ImpactConfirmDialog` pone las tres regiones,
          el padding correcto y el botón de peligro del sistema. */}
      <ImpactConfirmDialog
        open={Boolean(deactivating)}
        eyebrow="Círculo de miembros"
        title={`¿Inactivar a «${deactivating?.name ?? ""}»?`}
        message="El referido y su historial se conservan; solo deja de figurar como activo."
        confirmLabel="Sí, inactivar"
        busy={mutations.inactivate.isPending}
        onCancel={() => setDeactivating(null)}
        onConfirm={confirmDeactivate}
      />

      {openReferralId ? (
        <ReferralActionsPanel
          referralId={openReferralId}
          {...(member?.id ? { memberId: member.id } : {})}
          onClose={() => setOpenReferralId(null)}
        />
      ) : null}
    </div>
  );
}
